import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoginRequest, UserComplete } from '../models/auth.models';
import { BaseApiService } from './base-api.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseApiService {
  private apiUrl = `${environment.apiUrl}/Auth`;
  private currentUserKey = 'currentUser';
  private loggedIn = new BehaviorSubject<boolean>(this.isLoggedIn());

  isLoggedIn$ = this.loggedIn.asObservable();

  constructor(http: HttpClient) {
    super(http, 'Auth');
  }

  login(request: LoginRequest): Observable<HttpResponse<UserComplete>> {
    return this.http.post<UserComplete>(`${this.apiUrl}/login`, request, { observe: 'response' })
      .pipe(
        tap((response: HttpResponse<UserComplete>) => {
          if (response.ok && response.body) {
            const token = response.headers.get('jwt');
            const user = response.body;
            if (token) {
              user.token = token;
            }
            this.setCurrentUser(user);
            this.loggedIn.next(true);
          }
        })
      );
  }

  isLoggedIn(): boolean {
    return !!localStorage.getItem(this.currentUserKey);
  }

  logout(): void {
    localStorage.removeItem(this.currentUserKey);
    this.loggedIn.next(false);
  }

  private setCurrentUser(user: UserComplete): void {
    localStorage.setItem(this.currentUserKey, JSON.stringify(user));
  }

  getCurrentUser(): UserComplete | null {
    const user = localStorage.getItem(this.currentUserKey);
    return user ? JSON.parse(user) : null;
  }

  getAccount(userId: string): Observable<UserComplete> {
    return this.get<UserComplete>('GetAccount', { userId: userId });
  }

  getProfile(userId: string): Observable<UserComplete> {
    return this.get<UserComplete>('GetProfile', { userId: userId });
  }

  resendConsentment(): Observable<boolean> {
    return this.post<boolean>({}, 'ReSendConsentment');
  }

  saveProfilePhoto(file: File, id: string): Observable<any> {
    return new Observable(observer => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        const base64Data = base64.split(',')[1]; // Remove data:image/...;base64, prefix

        const request = {
          UserId: id,
          Base64: base64Data
        };

        this.http.post(`${this.baseUrl}/UpdatePhotoProfile`, request, { responseType: 'text' })
          .subscribe({
            next: (response) => observer.next(response),
            error: (error) => observer.error(error),
            complete: () => observer.complete()
          });
      };
      reader.onerror = () => observer.error('Error reading file');
      reader.readAsDataURL(file);
    });
  }

  forgotPassword(email: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/ForgotPassword`, {}, { params: { email } });


  }

}
